// Google Search Console on the Traffic screen: one "Google Search" card per project.
// Hidden when the Worker has no Google credentials, on org-level views and on share links.
// Owners and admins connect, pick the property and disconnect; viewers only read.
import type { ChartNote, ProjectInfo } from "../src/types";
import type { GscDay, GscReport, GscRow, GscSites, GscStatus } from "../src/gsc-types";
import { areaChart, sparkline } from "./chart";
import { fetchNotes } from "./notes";
import { shared } from "./share";

type Dim = "queries" | "pages" | "countries" | "devices";
type Metric = "clicks" | "impressions" | "ctr" | "position";

export interface SearchOpts {
  project: ProjectInfo;
  /** The screen's range (and filters, which Search Console data ignores). */
  params: Record<string, string>;
  mobile: boolean;
  current: () => boolean;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const fmtN = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toLocaleString("en-US"));
const fmtPct1 = (r: number | null) => (r === null ? "–" : `${(r * 100).toFixed(1)}%`);
const fmtPos = (p: number | null) => (p === null ? "–" : p.toFixed(1));
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (s: string) => `${Number(s.slice(8, 10))} ${MON[Number(s.slice(5, 7)) - 1]}`;
const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();
const countryName = (code: string) => (code ? (regionNames?.of(code) ?? code) : "Unknown");
const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);
/** "sc-domain:example.com" → "example.com (domain)". */
const siteLabel = (s: string) => (s.startsWith("sc-domain:") ? `${s.slice(10)} (domain)` : s);

const METRICS: { key: Metric; label: string; fmt: (v: number | null) => string; total: (t: GscReport["totals"]) => number | null; day: (d: GscDay) => number | null }[] = [
  { key: "clicks", label: "Clicks", fmt: (v) => (v === null ? "–" : fmtN(v)), total: (t) => t.clicks, day: (d) => d.clicks },
  { key: "impressions", label: "Impressions", fmt: (v) => (v === null ? "–" : fmtN(v)), total: (t) => t.impressions, day: (d) => d.impressions },
  { key: "ctr", label: "CTR", fmt: (v) => (v === null ? "–" : `${v.toFixed(1)}%`), total: (t) => (t.ctr === null ? null : t.ctr * 100), day: (d) => (d.ctr === null ? null : d.ctr * 100) },
  { key: "position", label: "Avg. position", fmt: fmtPos, total: (t) => t.position, day: (d) => d.position },
];
const DIMS: [string, Dim][] = [["Queries", "queries"], ["Pages", "pages"], ["Countries", "countries"], ["Devices", "devices"]];

const MESSAGES: Record<string, string> = {
  connected: "Search Console is connected. Pick the property that matches this site.",
  denied: "Google sign-in was cancelled, so nothing was connected.",
  scope_missing: "Google didn't grant access to Search Console. Connect again and leave the Search Console box ticked.",
  state_invalid: "That sign-in link didn't match this session. Start again from Connect Search Console.",
  state_expired: "The Google sign-in took too long. Start again from Connect Search Console.",
  failed: "Google didn't complete the connection. Try again in a minute.",
  gsc_no_site_access: "This Google account can't read the chosen property any more. Pick another one or reconnect with an account that can.",
  gsc_rate_limited: "Google is limiting requests right now. Try again in a few minutes.",
  gsc_unavailable: "Google Search Console didn't answer. Try again in a few minutes.",
  bad_site: "That property isn't available to the connected Google account.",
  read_only: "You have view-only access to this project. Ask an owner to change Search Console.",
};

// ---------- state (per page load; the screen's own state lives in the URL) ----------

let dim: Dim = "queries";
/** Tables show the top rows; "Show all" expands until the dimension or report changes. */
const TABLE_LIMIT = 25;
let showAll = false;
let drawnReport: GscReport | null = null;
let metric: Metric = "clicks";
let confirmDisconnect = false;
let changing = false;
let host: HTMLElement | null = null;
let last: SearchOpts | null = null;
let report: GscReport | null = null;
let status: GscStatus | null = null;
/** Chart notes for the report's range, drawn read-only on the Search chart (added on Overview). */
let chartNotes: ChartNote[] = [];
/** The result of a Google round trip (?gsc=...), shown once. */
let flash: string | null = (() => {
  const q = new URLSearchParams(location.search);
  const v = q.get("gsc");
  return v && /^[a-z_]{1,32}$/.test(v) ? v : null;
})();

async function call<T>(method: string, path: string, query: Record<string, string> = {}, body?: unknown): Promise<T> {
  const qs = Object.keys(query).length ? `?${new URLSearchParams(query)}` : "";
  const res = await fetch(`/api/gsc/${path}${qs}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const notice = (msg: string, error = false) => `<div class="notice ${error ? "error" : ""} gsc-note">${esc(msg)}</div>`;
const flashNote = () => {
  if (!flash) return "";
  const m = MESSAGES[flash];
  const ok = flash === "connected";
  flash = null;
  return m ? notice(m, !ok) : "";
};
const skeleton = (h: number) => `<div class="skeleton" style="height:${h}px"></div>`;

/** Fills (or hides) `el` with the Google Search card for the current project and range. */
export async function renderSearch(el: HTMLElement | null, o: SearchOpts): Promise<void> {
  if (!el) return;
  host = el;
  last = o;
  el.hidden = true;
  if (shared || o.project.kind === "org") return;
  injectCss();
  let st: GscStatus;
  try {
    st = await call<GscStatus>("GET", "status", { project: o.project.id });
  } catch {
    return; // The rest of Traffic matters more; leave the card out.
  }
  if (!o.current() || !st.configured) return;
  status = st;
  el.hidden = false;
  if (!st.connected) return void (el.innerHTML = shell(o, `${flashNote()}${connectBody(st)}`));
  if (st.reconnect)
    return void (el.innerHTML = shell(
      o,
      `${flashNote()}<div class="gsc-empty"><p>Google no longer accepts this project's Search Console connection (access was removed, or the password changed).</p>${
        st.can_manage ? `<button class="btn primary" data-gsc-act="connect">Reconnect Search Console</button>` : `<p class="gsc-muted">Ask an owner of this project to reconnect it.</p>`
      }</div>`,
      st,
    ));
  if (!st.site_url || changing) return pickSite(el, o, st);

  el.innerHTML = shell(o, skeleton(o.mobile ? 320 : 420), st);
  try {
    [report, chartNotes] = await Promise.all([
      call<GscReport>("GET", "report", { project: o.project.id, from: o.params.from ?? "", to: o.params.to ?? "" }),
      fetchNotes(o.project.id, o.params.from ?? "", o.params.to ?? ""),
    ]);
  } catch (err) {
    if (!o.current()) return;
    const code = (err as Error).message;
    if (code === "gsc_reconnect") return renderSearch(el, o);
    el.innerHTML = shell(o, `${notice(MESSAGES[code] ?? `Couldn't load Search Console data: ${code}`, true)}${
      st.can_manage && code === "gsc_no_site_access" ? `<div class="gsc-pick"><button class="btn" data-gsc-act="change">Pick another property</button><button class="btn" data-gsc-act="connect">Reconnect</button></div>` : ""
    }`, st);
    return;
  }
  if (!o.current()) return;
  draw(el, o, st, report);
}

function shell(o: SearchOpts, body: string, st?: GscStatus): string {
  const site = st?.site_url ? `<span class="gsc-site" title="${esc(st.site_url)}">${esc(siteLabel(st.site_url))}</span>` : "";
  const actions = st?.can_manage && st.connected ? ownerActions(st) : "";
  return `<section class="card gsc"><div class="card-head"><h2>Google Search</h2>${site}<span class="spacer"></span>${actions}</div>${body}</section>`;
}

function ownerActions(st: GscStatus): string {
  if (confirmDisconnect)
    return `<span class="card-actions"><span class="gsc-muted">Disconnect Search Console?</span><button class="link danger" data-gsc-act="disconnect-yes">Disconnect</button><button class="link" data-gsc-act="disconnect-no">Keep</button></span>`;
  return `<span class="card-actions">${st.site_url && !st.reconnect ? `<button class="link" data-gsc-act="change">Change property</button>` : ""}<button class="link" data-gsc-act="disconnect">Disconnect</button></span>`;
}

function connectBody(st: GscStatus): string {
  return `<div class="gsc-empty"><p>See the Google searches that bring people here: clicks, impressions, click-through rate and average position, by query, page, country and device.</p>${
    st.can_manage
      ? `<button class="btn primary" data-gsc-act="connect">Connect Search Console</button><p class="gsc-muted">You'll sign in with a Google account that can see this site in Search Console. MonoLog only gets read access.</p>`
      : `<p class="gsc-muted">Search Console isn't connected for this project. An owner of the project can connect it.</p>`
  }</div>`;
}

async function pickSite(el: HTMLElement, o: SearchOpts, st: GscStatus): Promise<void> {
  const top = flashNote();
  if (!st.can_manage) return void (el.innerHTML = shell(o, `${top}<p class="gsc-muted">Search Console is connected, but no property has been picked yet. An owner of the project can pick it.</p>`, st));
  el.innerHTML = shell(o, `${top}${skeleton(80)}`, st);
  let s: GscSites;
  try {
    s = await call<GscSites>("GET", "sites", { project: o.project.id });
  } catch (err) {
    if (!o.current()) return;
    const code = (err as Error).message;
    if (code === "gsc_reconnect") return renderSearch(el, o);
    el.innerHTML = shell(o, `${top}${notice(MESSAGES[code] ?? `Couldn't list Search Console properties: ${code}`, true)}`, st);
    return;
  }
  if (!o.current()) return;
  if (!s.sites.length) {
    el.innerHTML = shell(o, `${top}<div class="gsc-empty"><p>This Google account has no verified Search Console properties. Add and verify the site in Search Console, or reconnect with another account.</p><button class="btn" data-gsc-act="connect">Reconnect</button></div>`, st);
    return;
  }
  const chosen = st.site_url ?? s.suggested ?? s.sites[0]!.site_url;
  el.innerHTML = shell(
    o,
    `${top}<form class="gsc-pick" data-gsc-form>
      <label class="fld"><span>Property for ${esc(o.project.name)}</span>
      <select class="in" name="site">${s.sites.map((x) => `<option value="${esc(x.site_url)}"${x.site_url === chosen ? " selected" : ""}>${esc(siteLabel(x.site_url))}${x.site_url === s.suggested ? " · matches this project" : ""}</option>`).join("")}</select></label>
      <div class="gsc-btns"><button class="btn primary" type="submit">Use this property</button>${changing ? `<button class="btn" type="button" data-gsc-act="change-cancel">Cancel</button>` : ""}</div>
      <div class="form-err" hidden></div>
    </form>`,
    st,
  );
}

// ---------- the report ----------

function delta(m: Metric, cur: number | null, prev: number | null): string {
  if (cur === null || prev === null || (m !== "position" && prev === 0)) return `<span class="delta" title="No previous period to compare">–</span>`;
  if (m === "position") {
    // Lower is better; show the change in places.
    const d = prev - cur;
    const cls = Math.abs(d) < 0.05 ? "" : d > 0 ? "good" : "bad";
    return `<span class="delta ${cls}" title="Previous period: ${fmtPos(prev)}">${d >= 0 ? "↑" : "↓"} ${Math.abs(d).toFixed(1)}</span>`;
  }
  const pct = ((cur - prev) / prev) * 100;
  const cls = Math.round(pct) === 0 ? "" : pct > 0 ? "good" : "bad";
  return `<span class="delta ${cls}">${pct >= 0 ? "↑" : "↓"} ${Math.abs(pct).toFixed(0)}%</span>`;
}

const rowsFor = (r: GscReport): GscRow[] => ({ queries: r.queries, pages: r.pages, countries: r.countries, devices: r.devices })[dim];
const pathOf = (u: string) => {
  try {
    const x = new URL(u);
    return `${x.pathname}${x.search}` || "/";
  } catch {
    return u;
  }
};
function keyLabel(row: GscRow): string {
  if (dim === "countries") return `<span class="lbl center"><span class="iso">${esc(row.key || "??")}</span>${esc(countryName(row.key))}</span>`;
  if (dim === "pages") return `<span class="path" title="${esc(row.key)}">${esc(pathOf(row.key))}</span>`;
  if (dim === "devices") return `<span class="lbl">${esc(cap(row.key) || "Unknown")}</span>`;
  return `<span class="lbl gsc-q" title="${esc(row.key)}">${esc(row.key)}</span>`;
}
const HEAD: Record<Dim, string> = { queries: "Query", pages: "Page", countries: "Country", devices: "Device" };

function table(all: GscRow[], mobile: boolean): string {
  if (!all.length) return `<div class="empty">No search data for this range yet. Google reports it 2 to 3 days late.</div>`;
  const rows = showAll ? all : all.slice(0, TABLE_LIMIT);
  const more =
    all.length > TABLE_LIMIT
      ? `<button class="link gsc-more" data-gsc-more>${showAll ? "Show top " + TABLE_LIMIT : `Show all ${fmtN(all.length)}`}</button>`
      : "";
  return tableRows(rows, all, mobile) + more;
}

function tableRows(rows: GscRow[], all: GscRow[], mobile: boolean): string {
  // Bars scale to the whole list, so they don't jump when it expands.
  const max = Math.max(1, ...all.map((r) => r.clicks || r.impressions / 20));
  const w = (r: GscRow) => (((r.clicks || r.impressions / 20) / max) * 100).toFixed(1);
  if (mobile)
    return rows
      .map(
        (r) => `<div class="mbar"><div class="bar" style="width:${w(r)}%"></div><span class="l${dim === "pages" ? " path" : ""}">${
          dim === "countries" ? `<span class="iso">${esc(r.key || "??")}</span>${esc(countryName(r.key))}` : esc(dim === "pages" ? pathOf(r.key) : dim === "devices" ? cap(r.key) : r.key)
        }<span class="sub">${fmtN(r.impressions)} impr. · #${fmtPos(r.position)}</span></span><span class="v">${fmtN(r.clicks)}</span></div>`,
      )
      .join("");
  const grid = "grid-template-columns:minmax(0,1fr) 72px 96px 64px 72px";
  return `<div class="table-scroll"><div class="rows wide"><div class="rhead" style="${grid}"><span>${HEAD[dim]}</span><span>Clicks</span><span>Impressions</span><span>CTR</span><span>Position</span></div>${rows
    .map(
      (r) => `<div class="row" style="${grid}"><div class="barcell"><div class="bar" style="width:${w(r)}%"></div>${keyLabel(r)}</div><span class="n p">${fmtN(r.clicks)}</span><span class="n">${fmtN(r.impressions)}</span><span class="n">${fmtPct1(r.ctr)}</span><span class="n">${fmtPos(r.position)}</span></div>`,
    )
    .join("")}</div></div>`;
}

function draw(el: HTMLElement, o: SearchOpts, st: GscStatus, r: GscReport): void {
  if (r !== drawnReport) (showAll = false), (drawnReport = r);
  const M = METRICS.find((m) => m.key === metric)!;
  const filtered = Object.keys(o.params).some((k) => !["project", "from", "to"].includes(k));
  const lag = r.last_day
    ? r.last_day < r.range.to
      ? `Google reports search data 2 to 3 days late: latest day here is ${dayLabel(r.last_day)}.`
      : ""
    : "Google reports search data 2 to 3 days late, so there's nothing for this range yet.";
  const notes = [lag, filtered ? "These figures come from Google and ignore the dashboard's filters." : ""].filter(Boolean);
  const top = `${flashNote()}${notes.length ? `<p class="gsc-muted gsc-lag">${notes.join(" ")}</p>` : ""}`;
  const seg = o.mobile
    ? `<div class="mseg s" role="group">${DIMS.map(([l, v]) => `<button data-gsc-dim="${v}" aria-pressed="${v === dim}">${l}</button>`).join("")}</div>`
    : `<div class="seg small" role="group">${DIMS.map(([l, v]) => `<button data-gsc-dim="${v}" aria-pressed="${v === dim}">${l}</button>`).join("")}</div>`;
  // Sparklines stop at the last day Google has reported (they draw gaps as zeros).
  const reported = r.daily.filter((d) => r.last_day && d.day <= r.last_day);
  const kpis = METRICS.map((m) => {
    const cur = m.total(r.totals);
    const prev = m.total(r.previous);
    const sel = m.key === metric;
    return o.mobile
      ? `<button class="m-kpi" data-gsc-metric="${m.key}" aria-pressed="${sel}"><span class="label">${m.label}</span><span class="vr"><span class="v num">${m.fmt(cur)}</span>${delta(m.key, cur, prev)}</span></button>`
      : `<button class="kpi" data-gsc-metric="${m.key}" aria-pressed="${sel}"><span class="label">${m.label}</span><span class="value num">${m.fmt(cur)}</span>
        <span class="foot">${delta(m.key, cur, prev)}${reported.some((d) => m.day(d) !== null) ? sparkline(reported.map(m.day), sel ? "#2F5BD3" : "#A9B0BF") : ""}</span></button>`;
  }).join("");
  const exportBtn = `<button class="link" data-gsc-csv>Export CSV</button>`;
  const range = `${dayLabel(r.range.from)} – ${dayLabel(r.range.to)}`;
  el.innerHTML = shell(
    o,
    `${top}<div class="${o.mobile ? "m-kpis" : "kpis gsc4"}" role="group" aria-label="Search chart metric">${kpis}</div>
    <div class="gsc-chart">${
      o.mobile
        ? `<div class="m-chart-head"><h2>${M.label} per day</h2><span>${range}</span></div><div class="m-legend"><span><i class="cur"></i>This period</span><span><i class="prev"></i>Previous</span></div>`
        : `<div class="chart-head"><h2>${M.label} per day</h2><span class="spacer"></span><div class="legend"><span><i class="cur"></i>${range}</span><span><i class="prev"></i>Previous period</span></div></div>`
    }<div id="gsc-chart"></div></div>
    <div class="card-head gsc-tabs">${seg}<span class="spacer"></span>${exportBtn}</div>
    ${table(rowsFor(r), o.mobile)}`,
    st,
  );
  areaChart(document.getElementById("gsc-chart")!, {
    labels: r.daily.map((d) => dayLabel(d.day)),
    // Days Google hasn't reported yet are gaps, not zeros.
    current: r.daily.map((d) => (!r.last_day || d.day > r.last_day ? null : M.day(d))),
    previous: r.previous_daily.map(M.day),
    metric: M.label,
    format: (v) => M.fmt(v),
    compact: o.mobile,
    days: r.daily.map((d) => d.day),
    notes: chartNotes,
  });
}

function downloadCsv(): void {
  if (!report || !last) return;
  const rows = rowsFor(report);
  if (!rows.length) return;
  const cols = ["key", "clicks", "impressions", "ctr", "position"] as const;
  const cell = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [[dim.replace(/ies$/, "y").replace(/s$/, ""), ...cols.slice(1)].join(","), ...rows.map((r) => cols.map((c) => cell(r[c])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = `${last.project.id}-search-${dim}-${report.range.from}_${report.range.to}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

const rerender = () => (host && last ? renderSearch(host, last) : Promise.resolve());
const redraw = () => {
  if (host && last && status && report && last.current()) draw(host, last, status, report);
};

async function act(a: string): Promise<void> {
  if (!last) return;
  const project = last.project.id;
  confirmDisconnect = false;
  switch (a) {
    case "connect": {
      const { url } = await call<{ url: string }>("POST", "connect", {}, { project });
      location.href = url;
      return;
    }
    case "change": changing = true; break;
    case "change-cancel": changing = false; break;
    case "disconnect": confirmDisconnect = true; break;
    case "disconnect-no": break;
    case "disconnect-yes": await call("DELETE", "connection", { project }); changing = false; report = null; break;
  }
  await rerender();
}

document.addEventListener("click", (ev) => {
  const t = ev.target as HTMLElement;
  const a = t.closest<HTMLElement>("[data-gsc-act]");
  if (a)
    return void act(a.dataset.gscAct!).catch((e) => {
      host?.insertAdjacentHTML("afterbegin", notice(MESSAGES[(e as Error).message] ?? `Couldn't do that: ${(e as Error).message}`, true));
    });
  const d = t.closest<HTMLElement>("[data-gsc-dim]");
  if (d) return void ((dim = d.dataset.gscDim as Dim), (showAll = false), redraw());
  if (t.closest("[data-gsc-more]")) return void ((showAll = !showAll), redraw());
  const m = t.closest<HTMLElement>("[data-gsc-metric]");
  if (m) return void ((metric = m.dataset.gscMetric as Metric), redraw());
  if (t.closest("[data-gsc-csv]")) downloadCsv();
});

document.addEventListener("submit", (ev) => {
  const form = (ev.target as HTMLElement).closest<HTMLFormElement>("form[data-gsc-form]");
  if (!form || !last) return;
  ev.preventDefault();
  const site = String(new FormData(form).get("site") ?? "");
  call("PUT", "site", {}, { project: last.project.id, site_url: site })
    .then(() => {
      changing = false;
      return rerender();
    })
    .catch((e) => {
      const err = form.querySelector<HTMLElement>(".form-err")!;
      err.textContent = MESSAGES[(e as Error).message] ?? `Couldn't save: ${(e as Error).message}`;
      err.hidden = false;
    });
});

let cssDone = false;
/** The few styles this card adds on top of the shared card, KPI, chart and table styles. */
function injectCss(): void {
  if (cssDone) return;
  cssDone = true;
  const s = document.createElement("style");
  s.textContent = `
.gsc .kpis.gsc4 { grid-template-columns: repeat(4, minmax(132px, 1fr)); border: 1px solid var(--divider); border-radius: 12px; overflow: hidden; }
.gsc .gsc-site { font-family: var(--mono); font-size: 12px; color: var(--muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.gsc .gsc-muted { color: var(--muted); font-size: 13px; margin: 0; }
.gsc .gsc-lag { margin: -6px 0 12px; }
.gsc .gsc-more { display: block; margin: 12px auto 0; min-height: 44px; }
.gsc .gsc-empty { display: flex; flex-direction: column; align-items: flex-start; gap: 12px; }
.gsc .gsc-empty p { margin: 0; max-width: 62ch; }
.gsc .gsc-pick { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.gsc .gsc-pick .fld { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: var(--text-2); width: min(100%, 460px); }
.gsc .gsc-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.gsc .gsc-note { margin-bottom: 12px; padding: 10px 16px; font-size: 13px; }
.gsc .gsc-chart { margin: 20px 0 8px; }
.gsc .gsc-tabs { margin-top: 12px; }
.gsc .gsc-q { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
@media (max-width: 640px) {
  .gsc .gsc-chart { margin: 14px 0 4px; }
  .gsc .mseg { width: 100%; }
  .gsc .gsc-btns .btn, .gsc .gsc-empty .btn { min-height: 44px; }
  .gsc .gsc-pick .fld { width: 100%; }
}`;
  document.head.append(s);
}
